import { useState, useRef, useCallback } from 'react';
import API_CONFIG from '../config/api';

/**
 * Хук для потокового чата с поддержкой SSE
 */
const useStreamingChat = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingError, setStreamingError] = useState(null);
  const [tokenCount, setTokenCount] = useState(0);
  
  const abortControllerRef = useRef(null);
  const streamStateRef = useRef({
    isActive: false,
    accumulated: '',
    startTime: null,
    messageId: null
  });

  // Cleanup функция
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    streamStateRef.current = {
      isActive: false,
      accumulated: '',
      startTime: null,
      messageId: null
    };
    
    setIsStreaming(false);
    setStreamingText('');
    setStreamingError(null);
    setTokenCount(0);
  }, []);

  // Обработчик событий потока
  const handleStreamEvent = useCallback((eventData) => {
    if (!streamStateRef.current.isActive) {
      return;
    }

    switch (eventData.type) {
      case 'content':
        streamStateRef.current.accumulated = eventData.fullText || streamStateRef.current.accumulated + eventData.content;
        setStreamingText(streamStateRef.current.accumulated);
        if (eventData.tokenCount) {
          setTokenCount(eventData.tokenCount);
        }
        break;
        
      case 'stream_done':
        streamStateRef.current.accumulated = eventData.content;
        setStreamingText(eventData.content);
        if (eventData.tokenCount) {
          setTokenCount(eventData.tokenCount);
        }
        // Завершение потока обрабатывается в основной функции
        break;
        
      case 'complete':
        // console.log('🏁 [COMPLETE] Получено событие завершения потока:', eventData);
        streamStateRef.current.accumulated = eventData.content;
        setStreamingText(eventData.content);
        if (eventData.tokenCount) {
          setTokenCount(eventData.tokenCount);
        }
        // Сохраняем данные чата для передачи в callback
        streamStateRef.current.updatedChat = eventData.chat;
        break;
        
      case 'error':
        setStreamingError(eventData.error);
        // console.error('Stream error:', eventData.error);
        break;
        
      default:
        // console.log('Unknown stream event type:', eventData.type);
    }
  }, []);

  const startStreaming = useCallback(async (chatId, aiMessage, systemPrompt = '', userMessage = null, onComplete = null, onError = null, apiModelId = null, displayContent = null, specificFileNames = null) => {
    if (isStreaming) {
      console.warn('⚠️ [STREAM] Stream already active');
      return;
    }

    // console.log('🌊 [STREAM START] Запуск потоковой передачи:', {
    //   chatId,
    //   aiMessage: aiMessage.substring(0, 50) + '...',
    //   userMessage: userMessage ? userMessage.substring(0, 50) + '...' : 'не задано',
    //   hasSystemPrompt: !!systemPrompt
    // });

    // Сбрасываем предыдущие состояния
    cleanup();
    
    try {
      setIsStreaming(true);
      setStreamingError(null);
      
      // Создаем новый AbortController
      abortControllerRef.current = new AbortController();
      
      // Инициализируем состояние потока
      streamStateRef.current = {
        isActive: true,
        accumulated: '',
        startTime: Date.now(),
        messageId: chatId
      };

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const streamUrl = `${API_CONFIG.getBaseURL()}/api/chat/${chatId}/messages/stream`;
      // console.log('🔗 [FETCH] Starting stream to URL:', streamUrl);
      
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: API_CONFIG.getStreamingHeaders(token),
        body: JSON.stringify({
          aiMessage,  // Для ИИ (полное содержимое файлов)
          userMessage: userMessage || aiMessage, // Для интерфейса (название файла + промпт)
          systemPrompt: systemPrompt || undefined,
          apiModelId: apiModelId || undefined,
          displayContent: displayContent || undefined, // Краткое отображение для UI
          specificFileNames: specificFileNames || undefined // Конкретные файлы для добавления
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is empty');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (!streamStateRef.current.isActive) {
          console.log('🛑 [STREAM] Stream stopped by user');
          break;
        }

        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ [STREAM] Stream completed naturally');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Сохраняем неполную строку

        for (const line of lines) {
          if (!line.trim()) continue;
          
          try {
            if (line.startsWith('event:')) {
              // Обрабатываем event заголовок
              continue;
            }
            
            if (line.startsWith('data:')) {
              const jsonStr = line.substring(5).trim();
              if (jsonStr) {
                const eventData = JSON.parse(jsonStr);
                handleStreamEvent(eventData);
                
                // Если получили complete, завершаем поток
                if (eventData.type === 'complete') {
                  streamStateRef.current.isActive = false;
                  if (onComplete) {
                    onComplete(eventData.content, {
                      tokenCount: eventData.tokenCount,
                      duration: Date.now() - streamStateRef.current.startTime
                    }, eventData.chat); // Передаем обновленный чат
                  }
                  return eventData.content;
                }
                
                // Если получили stream_done (для обратной совместимости), завершаем поток
                if (eventData.type === 'stream_done') {
                  streamStateRef.current.isActive = false;
                  if (onComplete) {
                    onComplete(eventData.content, {
                      tokenCount: eventData.tokenCount,
                      duration: Date.now() - streamStateRef.current.startTime
                    }, streamStateRef.current.updatedChat); // Передаем сохраненный чат
                  }
                  return eventData.content;
                }
              }
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError, 'Line:', line);
          }
        }
      }

      return streamStateRef.current.accumulated;

    } catch (error) {
      console.error('Streaming error:', error);
      
      if (error.name === 'AbortError') {
        console.log('Stream was aborted');
        return streamStateRef.current.accumulated;
      }
      
      setStreamingError(error.message);
      if (onError) {
        onError(error);
      }
      throw error;
    } finally {
      setIsStreaming(false);
      streamStateRef.current.isActive = false;
      // Сбрасываем streamingText после завершения потока
      setStreamingText('');
    }
  }, [isStreaming, cleanup, handleStreamEvent]);

  const stopStreaming = useCallback(() => {
    // console.log('Stopping stream...');
    streamStateRef.current.isActive = false;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setIsStreaming(false);
  }, []);

  // Cleanup при размонтировании
  const resetStream = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // Новый метод для синхронной обработки файлов с промптом
  const startStreamingWithFiles = useCallback(async (chatId, files, message, systemPrompt = '', onComplete = null, onError = null, apiModelId = null) => {
    if (isStreaming) {
      console.warn('⚠️ [STREAM] Stream already active');
      return;
    }

    // console.log('📁 [STREAM WITH FILES] Запуск потоковой передачи с файлами:', {
    //   chatId,
    //   filesCount: files.length,
    //   message: message ? message.substring(0, 50) + '...' : 'без промпта',
    //   hasSystemPrompt: !!systemPrompt
    // });

    // Сбрасываем предыдущие состояния
    cleanup();
    
    try {
      setIsStreaming(true);
      setStreamingError(null);
      
      // Создаем новый AbortController
      abortControllerRef.current = new AbortController();
      
      // Инициализируем состояние потока
      streamStateRef.current = {
        isActive: true,
        accumulated: '',
        startTime: Date.now(),
        messageId: chatId
      };

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Создаем FormData для отправки файлов
      const formData = new FormData();
      files.forEach(fileData => {
        const file = fileData.file ? fileData.file : fileData;
        formData.append('files', file);
      });
      
      // Добавляем текстовые параметры
      if (message) formData.append('message', message);
      if (systemPrompt) formData.append('systemPrompt', systemPrompt);
      if (apiModelId) formData.append('apiModelId', apiModelId);

      const streamUrl = `${API_CONFIG.getBaseURL()}/api/chat/${chatId}/messages/stream-with-files`;
      // console.log('🔗 [FETCH] Starting file stream to URL:', streamUrl);
      
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // НЕ добавляем Content-Type для FormData - браузер добавит с boundary автоматически
        },
        body: formData,
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is empty');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (!streamStateRef.current.isActive) {
          console.log('🛑 [STREAM] Stream stopped by user');
          break;
        }

        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ [STREAM] Stream completed naturally');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Сохраняем неполную строку

        for (const line of lines) {
          if (!line.trim()) continue;
          
          try {
            if (line.startsWith('event:')) {
              // Обрабатываем event заголовок
              continue;
            }
            
            if (line.startsWith('data:')) {
              const jsonStr = line.substring(5).trim();
              if (jsonStr) {
                const eventData = JSON.parse(jsonStr);
                
                // Специальная обработка событий файлов
                if (eventData.type === 'files_processing_start') {
                  setStreamingText(`Обрабатываем ${eventData.filesCount} файлов...`);
                  continue;
                }
                
                if (eventData.type === 'file_processing') {
                  setStreamingText(`Обрабатываем файл "${eventData.fileName}" (${eventData.fileIndex}/${eventData.totalFiles})...`);
                  continue;
                }
                
                if (eventData.type === 'files_processing_complete') {
                  setStreamingText(`Файлы обработаны. Генерируем ответ...`);
                  continue;
                }
                
                if (eventData.type === 'ai_generation_start') {
                  setStreamingText('');
                  continue;
                }
                
                // Обычная обработка streaming событий
                handleStreamEvent(eventData);
                
                // Если получили complete, завершаем поток
                if (eventData.type === 'complete') {
                  streamStateRef.current.isActive = false;
                  if (onComplete) {
                    onComplete(eventData.content, {
                      tokenCount: eventData.tokenCount,
                      duration: Date.now() - streamStateRef.current.startTime
                    }, eventData.chat); // Передаем обновленный чат
                  }
                  return eventData.content;
                }
              }
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError, 'Line:', line);
          }
        }
      }

      return streamStateRef.current.accumulated;

    } catch (error) {
      console.error('Streaming with files error:', error);
      
      if (error.name === 'AbortError') {
        console.log('Stream was aborted');
        return streamStateRef.current.accumulated;
      }
      
      setStreamingError(error.message);
      if (onError) {
        onError(error);
      }
      throw error;
    } finally {
      setIsStreaming(false);
      streamStateRef.current.isActive = false;
      // Сбрасываем streamingText после завершения потока
      setStreamingText('');
    }
  }, [isStreaming, cleanup, handleStreamEvent]);

  return {
    isStreaming,
    streamingText,
    streamingError,
    tokenCount,
    startStreaming,
    startStreamingWithFiles, // Новый метод
    stopStreaming,
    resetStream,
    currentText: streamStateRef.current?.accumulated || ''
  };
};

export default useStreamingChat; 